import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Spinner } from 'react-bootstrap';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend
} from 'recharts';
import axios from '../api/axios';
import OutletSelect from './OutletSelect';

const KpiCard = ({ title, value, valueColor, icon, backgroundColor, loading }) => (
  <Card className="h-100 shadow-sm border-0 transition-all">
    <Card.Body
      className="d-flex flex-column justify-content-between p-3"
      style={{ backgroundColor }}
    >
      <div>
        {/* Title */}
        <Card.Title className="text-muted mb-2" style={{ fontSize: '0.9rem', fontWeight: '500' }}>
          {title}
        </Card.Title>
        {/* Value */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '40px' }}>
            <Spinner animation="border" size="sm" variant={valueColor} />
          </div>
        ) : (
          <Card.Text as="h4" style={{ color: valueColor, fontWeight: '600' }}>
            {value}
          </Card.Text>
        )}
      </div>
      {/* Icon */}
      <div className="text-end">
        <i className={`bi ${icon} fs-3`} style={{ color: valueColor }}></i>
      </div>
    </Card.Body>
  </Card>
);

const UsersKPI = () => {
  const token = localStorage.getItem('authToken');
  const [totalUsersData, setTotalUsersData] = useState({});
  const [cityWiseUsers, setCityWiseUsers] = useState([]);
  const [repeatingUsersTrend, setRepeatingUsersTrend] = useState([]);
  const [outletFilter, setOutletFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('weekly');
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState({
    totalData: true,
    graphData: true,
    outlets: true
  });
  const [dateFilter, setDateFilter] = useState('today');

  const DATE_FILTER_OPTIONS = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 30 Days', value: 'last30days' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
  ]

  const fetchGraphData = async (date, outlet) => {
    try {
      setLoading(prev => ({ ...prev, graphData: true }));
      let url = 'dashboard/getUsersDataStats';
      let params = [];
      if (outlet !== 'all') {
        params.push(`outletId=${outlet}`);
      }
      if (date) {
        params.push(`dateFilter=${date}`);
      }
      // if (timeFilter === 'weekly') {
      //   params.push("weekly=true");
      // } else if (timeFilter === 'monthly') {
      //   params.push("monthly=true");
      // }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.data?.data) {
        const data = response.data.data;
        // const barChartData = [
        //   {
        //     city: 'Total',
        //     appUsers: data.totalNumberOfAppUsers?.[0]?.count || 0,
        //     kioskUsers: data.totalNumberOfKioskUsers?.[0]?.count || 0,
        //     deletedUsers: data.totalNumberOfDeletedUsers?.[0]?.count || 0,
        //     repeatingUsers: data.totalRepeatingUsers?.[0]?.count || 0,
        //     referralUsers: data.totalReferredUsers?.[0]?.count || 0,
        //     usersWithoutOrders: data.totalUsersWithoutOrders?.[0]?.count || 0,
        //   },
        // ];

        // Helper function to sum all counts in an array
        const sumCounts = (arr) => {
          if (!arr || !Array.isArray(arr)) return 0;
          return arr.reduce((total, item) => total + (item?.count || 0), 0);
        };

        const barChartData = [
          {
            city: 'Total',
            appUsers: sumCounts(data.totalNumberOfAppUsers),
            kioskUsers: sumCounts(data.totalNumberOfKioskUsers),
            deletedUsers: sumCounts(data.totalNumberOfDeletedUsers),
            repeatingUsers: sumCounts(data.totalRepeatingUsers),
            referralUsers: sumCounts(data.totalReferredUsers),
            usersWithoutOrders: sumCounts(data.totalUsersWithoutOrders),
          },
        ];
        setCityWiseUsers(barChartData);

        const trendData = [
          {
            date: new Date().toLocaleDateString(),
            repeatingUsers: data.totalRepeatingUsers?.[0]?.count || 0,
          },
        ];
        setRepeatingUsersTrend(trendData);
      }
    } catch (error) {
      console.error('Error fetching graph data:', error);
    } finally {
      setLoading(prev => ({ ...prev, graphData: false }));
    }
  };

  const fetchTotalUsersKPIData = async () => {
    try {
      setLoading(prev => ({ ...prev, totalData: true }));
      const url = 'dashboard/getUsersDataStats';
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response?.data?.data) {
        setTotalUsersData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching total users data:', error);
    } finally {
      setLoading(prev => ({ ...prev, totalData: false }));
    }
  };

  const getOutlets = async () => {
    try {
      setLoading(prev => ({ ...prev, outlets: true }));
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get('outlet', { headers });
      if (response?.data?.status === true) {
        setOutlets(response?.data?.data?.outlets);
      }
    } catch (error) {
      console.error('Error fetching outlets:', error);
    } finally {
      setLoading(prev => ({ ...prev, outlets: false }));
    }
  };

  useEffect(() => {
    fetchTotalUsersKPIData();
    getOutlets();
    fetchGraphData(dateFilter, outletFilter);
  }, []);

  // useEffect(() => {
  //   fetchGraphData(dateFilter, outletFilter);
  // }, [dateFilter, outletFilter]);

  const handleDateFilterChange = async (e) => {
    const selectedDateFilter = e.target.value;
    setDateFilter(selectedDateFilter);
    fetchGraphData(selectedDateFilter, outletFilter);
  }

  const handleOutletFilterChange = async (e) => {
    const selectedOutletFilter = e.target.value;
    setOutletFilter(selectedOutletFilter);
    fetchGraphData(dateFilter, selectedOutletFilter);
  }

  return (
    <Container fluid className="py-4 shadow-sm" style={{ backgroundColor: 'white' }}>
      <h4 className="mb-4 fw-bold">USER ANALYTICS</h4>
      <hr />
      {/* Overall Users Data Section */}
      <Card className="mb-4 shadow-sm">
        <Card.Header as="h5" className="bg-primary text-white">
          Overall Users Data
        </Card.Header>
        <Card.Body>
          {loading.totalData ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Row className="g-4 justify-content-center">
              {/* Total App Users */}
              <Col md={3} sm={6}>
                <KpiCard
                  title="Total App Users"
                  value={totalUsersData?.totalNumberOfAppUsers?.[0]?.count || 0}
                  valueColor="#007bff"
                  icon="bi-phone"
                  backgroundColor="rgba(0, 123, 255, 0.1)"
                  loading={loading.totalData}
                />
              </Col>

              {/* Total Kiosk Users */}
              <Col md={3} sm={6}>
                <KpiCard
                  title="Total Kiosk Users"
                  value={totalUsersData?.totalNumberOfKioskUsers?.[0]?.count || 0}
                  valueColor="#28a745"
                  icon="bi-display"
                  backgroundColor="rgba(40, 167, 69, 0.1)"
                  loading={loading.totalData}
                />
              </Col>

              {/* Total Deleted Users */}
              <Col md={3} sm={6}>
                <KpiCard
                  title="Total Deleted Users"
                  value={totalUsersData?.totalNumberOfDeletedUsers?.[0]?.count || 0}
                  valueColor="#dc3545"
                  icon="bi-trash"
                  backgroundColor="rgba(220, 53, 69, 0.1)"
                  loading={loading.totalData}
                />
              </Col>

              {/* Total Referred Users */}
              <Col md={3} sm={6}>
                <KpiCard
                  title="Total Referred Users"
                  value={totalUsersData?.totalReferredUsers?.[0]?.count || 0}
                  valueColor="#6f42c1"
                  icon="bi-people"
                  backgroundColor="rgba(111, 66, 193, 0.1)"
                  loading={loading.totalData}
                />
              </Col>

              {/* Total Repeating Users */}
              <Col md={3} sm={6}>
                <KpiCard
                  title="Total Repeating Users"
                  value={totalUsersData?.totalRepeatingUsers?.[0]?.count || 0}
                  valueColor="#fd7e14"
                  icon="bi-arrow-repeat"
                  backgroundColor="rgba(253, 126, 20, 0.1)"
                  loading={loading.totalData}
                />
              </Col>

              {/* Total Users Without Orders */}
              <Col md={3} sm={6}>
                <KpiCard
                  title="Total Users Without Orders"
                  value={totalUsersData?.totalUsersWithoutOrders?.[0]?.count || 0}
                  valueColor="#17a2b8"
                  icon="bi-cart-x"
                  backgroundColor="rgba(23, 162, 184, 0.1)"
                  loading={loading.totalData}
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Weekly/Monthly Users Data Section */}
      <Card className="shadow-sm">
        <Card.Header as="h5" className="bg-secondary text-white">
          {timeFilter === 'weekly' ? 'Weekly Users Data' : 'Monthly Users Data'}
        </Card.Header>
        <Card.Body className='bg-white'>
          <Row className="mb-4">
            <Col md={4}>
    <Form.Group>
      <Form.Label className="text-muted fw-semibold">Outlet</Form.Label>

      <div className="d-flex align-items-center bg-light p-1 ps-2 rounded">
        <i className="bi bi-shop me-2 text-primary" /> {/* Icon added */}

        <Form.Select
          value={outletFilter}
          onChange={handleOutletFilterChange}
          className="border-0 bg-light"
        >
          <option value="all">All</option>
          {outlets
            ?.filter((outlet) => outlet?.status === 'ACTIVE')
            .map((outlet) => (
              <option key={outlet?._id} value={outlet?._id}>
                {outlet?.name}
              </option>
            ))}
        </Form.Select>
      </div>
    </Form.Group>
  </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className='text-muted fw-semibold'>Duration</Form.Label>
                {/* <Form.Select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </Form.Select> */}
                <div className="d-flex align-items-center bg-light p-1 ps-2 rounded">
                  <i className="bi bi-calendar-event me-2 text-primary" />
                  <Form.Select
                    value={dateFilter}
                    onChange={handleDateFilterChange}
                    style={{ width: '145px' }}
                    className="border-0 bg-light"
                  >
                    {DATE_FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <h5 className="mb-3">Users Type</h5>
              {loading.graphData ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                  <Spinner animation="border" variant="secondary" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={cityWiseUsers}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="city" />
                    <YAxis />
                    <Tooltip />
                    <Legend wrapperStyle={{ marginLeft: 20 }} />
                    <Bar dataKey="appUsers" fill="#8884d8" name="App" />
                    <Bar dataKey="kioskUsers" fill="#82ca9d" name="Kiosk" />
                    <Bar dataKey="deletedUsers" fill="#ffc658" name="Deleted" />
                    <Bar dataKey="repeatingUsers" fill="#ff7300" name="Repeating" />
                    <Bar dataKey="referralUsers" fill="#d27300" name="Referred" />
                    <Bar dataKey="usersWithoutOrders" fill="#d62728" name="Without Orders" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UsersKPI;